//////////////////////////////////////////////////////////////////////////////
//
//  광고주 추가 팝업
//
//  작성자: MediInStep Team
//  생성일: 2025-12-19
//
//  설명: 광고주 등록을 위한 팝업 다이얼로그
//
//////////////////////////////////////////////////////////////////////////////

import { Component } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Grid from "@mui/material/Grid";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import { request } from "app/network";
import Swal from "sweetalert2";
import { MaterialUI } from "context";

interface AddAdvertiserDialogProps {
    open: boolean;
    editingAdvertiser?: any | null;
    onClose: () => void;
    onSuccess: () => void;
    darkMode?: boolean;
    theme?: any;
}

interface AddAdvertiserDialogState {
    name: string;
    description: string;
    managerName: string;
    managerContact: string;
    isActive: boolean;
    loading: boolean;
    darkMode: boolean;
    tenantId: number | null;
    tenants: Array<{ tenantId: number; tenantName: string }>;
    seqId?: number | null;
}

class AddAdvertiserDialogClass extends Component<AddAdvertiserDialogProps, AddAdvertiserDialogState> {
    static contextType = MaterialUI;

    constructor(props: AddAdvertiserDialogProps) {
        super(props);
        const initialDarkMode = props.darkMode ?? false;

        this.state = {
            name: "",
            description: "",
            managerName: "",
            managerContact: "",
            isActive: true,
            loading: false,
            darkMode: initialDarkMode,
            tenantId: null,
            tenants: [],
            seqId: null,
        };
    }

    async componentDidMount() {
        // 로그인 시 선택한 테넌트 가져오기
        const SELECTED_TENANT_KEY = "mediinstep_selected_tenant";
        const savedTenantId = localStorage.getItem(SELECTED_TENANT_KEY);

        if (savedTenantId) {
            // 테넌트 정보 조회
            try {
                const param = {
                    PageNumber: 1,
                    PageSize: 1000,
                };

                const response = await request("Tenant.pagedList", { paramz: param });

                if (response && response.data && response.data.items) {
                    const tenants = response.data.items.map((tenant: any) => ({
                        tenantId: tenant.seqId,
                        tenantName: tenant.name || "이름 없음",
                    }));

                    // 저장된 테넌트 찾기
                    const selectedTenant = tenants.find(
                        (t: { tenantId: number; tenantName: string }) => t.tenantId.toString() === savedTenantId
                    );

                    if (selectedTenant) {
                        this.setState({
                            tenants,
                            tenantId: selectedTenant.tenantId,
                        });
                    } else {
                        this.setState({ tenants });
                    }
                } else {
                    console.log("No tenant data found in response");
                }
            } catch (error) {
                console.error("테넌트 목록 불러오기 실패:", error);
            }
        }
    }

    componentDidUpdate(prevProps: AddAdvertiserDialogProps) {
        // props에서 darkMode 변경 감지
        const context = this.context as any;
        const currentDarkMode = this.props.darkMode ?? context?.darkMode ?? false;

        if (currentDarkMode !== this.state.darkMode) {
            this.setState({ darkMode: currentDarkMode });
        }

        if (this.props.open && !prevProps.open) {
            // 다이얼로그가 열릴 때 필드 초기화 또는 수정 데이터 로드
            const SELECTED_TENANT_KEY = "mediinstep_selected_tenant";
            const savedTenantId = localStorage.getItem(SELECTED_TENANT_KEY);
            const tenantIdToUse = savedTenantId ? parseInt(savedTenantId, 10) : this.state.tenantId;

            if (this.props.editingAdvertiser) {
                // 수정 모드
                const advertiser = this.props.editingAdvertiser;
                this.setState({
                    seqId: advertiser.seqId,
                    name: advertiser.name || "",
                    description: advertiser.description || "",
                    managerName: advertiser.managerName || "",
                    managerContact: advertiser.managerContact || "",
                    isActive: advertiser.status === "ACTIVE",
                    loading: false,
                    darkMode: currentDarkMode,
                    tenantId: advertiser.tenantSeqId || tenantIdToUse,
                });
            } else {
                // 추가 모드
                this.setState({
                    seqId: null,
                    name: "",
                    description: "",
                    managerName: "",
                    managerContact: "",
                    isActive: true,
                    loading: false,
                    darkMode: currentDarkMode,
                    tenantId: tenantIdToUse,
                });
            }
        }
    }

    handleChange = (field: keyof AddAdvertiserDialogState, value: any) => {
        this.setState({ [field]: value } as any);
    };

    handleSubmit = async () => {
        const { name, description, managerName, managerContact, isActive, tenantId, seqId } = this.state;
        const { theme, darkMode: propsDarkMode } = this.props;
        const { darkMode: stateDarkMode } = this.state;
        const context = this.context as any;
        const finalDarkMode = propsDarkMode ?? stateDarkMode ?? context?.darkMode ?? false;

        // 필수 값 체크
        if (!tenantId) {
            this.showAlert("입력 오류", "테넌트를 선택해주세요.", "error", finalDarkMode, theme);
            return;
        }

        if (!name.trim()) {
            this.showAlert("입력 오류", "광고주 이름을 입력해주세요.", "error", finalDarkMode, theme);
            return;
        }

        try {
            this.setState({ loading: true });

            const param = {
                TenantSeqId: tenantId,
                CompanyName: name,
                ContactPhone: managerContact || "000-0000-0000",
                ContactName: managerName || undefined,
                Memo: description || undefined,
                Status: isActive ? "ACTIVE" : "INACTIVE",
                IsActive: isActive,
            };

            if (seqId) {
                // 수정 모드
                console.log("Updating advertiser with param:", param);
                await request("Advertiser.update", {
                    paramz: param,
                    pathParams: { id: seqId.toString() }
                });
                this.showAlert("수정 완료", "광고주가 성공적으로 수정되었습니다.", "success", finalDarkMode, theme, () => {
                    this.props.onSuccess();
                    this.props.onClose();
                });
            } else {
                // 추가 모드
                console.log("Creating advertiser with param:", param);
                await request("Advertiser.create", { paramz: param });
                this.showAlert("등록 완료", "광고주가 성공적으로 등록되었습니다.", "success", finalDarkMode, theme, () => {
                    this.props.onSuccess();
                    this.props.onClose();
                });
            }

        } catch (error: any) {
            console.error("광고주 저장 실패:", error);
            const errorMessage = error.response?.data?.message || "광고주 저장 중 오류가 발생했습니다.";
            this.showAlert("저장 실패", errorMessage, "error", finalDarkMode, theme);
        } finally {
            this.setState({ loading: false });
        }
    };

    showAlert = (title: string, text: string, icon: "success" | "error" | "warning" | "info", darkMode: boolean, theme: any, callback?: () => void) => {
        Swal.fire({
            icon: icon,
            title: title,
            text: text,
            confirmButtonText: "확인",
            background: darkMode ? theme?.palette?.background?.default || "#1a1a1a" : "#ffffff",
            color: darkMode ? "#ffffff" : "#000000",
            confirmButtonColor: theme?.palette?.info?.main || "#1976d2",
            customClass: {
                container: "swal2-container-high-zindex",
            },
            didOpen: () => {
                const swalContainer = document.querySelector(".swal2-container-high-zindex") as HTMLElement;
                if (swalContainer) {
                    swalContainer.style.zIndex = "1400";
                }
            },
        }).then(() => {
            if (callback) callback();
        });
    };

    render() {
        const { open, onClose, theme, darkMode: propsDarkMode } = this.props;
        const { name, description, managerName, managerContact, isActive, loading, darkMode: stateDarkMode } = this.state;

        const context = this.context as any;
        const finalDarkMode = propsDarkMode ?? stateDarkMode ?? context?.darkMode ?? false;

        const backgroundColor = finalDarkMode ? "#1a2035" : "#ffffff";
        const inputBackgroundColor = finalDarkMode ? "#1a2035" : "#ffffff";

        const inputSx = {
            "& .MuiOutlinedInput-root": {
                backgroundColor: `${inputBackgroundColor} !important`,
                "& fieldset": {
                    borderColor: `${finalDarkMode ? "rgba(255, 255, 255, 0.23)" : "rgba(0, 0, 0, 0.23)"} !important`,
                },
                "&:hover fieldset": {
                    borderColor: `${finalDarkMode ? "rgba(255, 255, 255, 0.5)" : "rgba(0, 0, 0, 0.5)"} !important`,
                },
                "&.Mui-focused fieldset": {
                    borderColor: `${theme?.palette?.info?.main || "#1976d2"} !important`,
                },
            },
            "& .MuiInputLabel-root": {
                color: `${finalDarkMode ? "rgba(255, 255, 255, 0.7)" : "rgba(0, 0, 0, 0.6)"} !important`,
            },
            "& .MuiInputBase-input": {
                color: `${finalDarkMode ? "#ffffff" : "#000000"} !important`,
                backgroundColor: "transparent !important",
            },
        };

        return (
            <Dialog
                open={open}
                onClose={onClose}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: { backgroundColor: backgroundColor },
                }}
            >
                <DialogTitle sx={{ backgroundColor: backgroundColor, padding: "24px 24px 16px 24px" }}>
                    <MDTypography component="span" fontWeight="medium" sx={{ fontSize: "1.25rem" }}>
                        {this.state.seqId ? '광고주 수정' : '광고주 추가'}
                    </MDTypography>
                </DialogTitle>
                <DialogContent sx={{ backgroundColor: backgroundColor, padding: "16px 24px" }}>
                    <MDBox pt={1} pb={1}>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <MDInput
                                    label="광고주 이름 *"
                                    fullWidth
                                    value={name}
                                    onChange={(e: any) => this.handleChange("name", e.target.value)}
                                    sx={inputSx}
                                    autoComplete="off"
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <MDInput
                                    label="설명"
                                    fullWidth
                                    multiline
                                    rows={3}
                                    value={description}
                                    onChange={(e: any) => this.handleChange("description", e.target.value)}
                                    sx={inputSx}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <MDInput
                                    label="담당자 이름"
                                    fullWidth
                                    value={managerName}
                                    onChange={(e: any) => this.handleChange("managerName", e.target.value)}
                                    sx={inputSx}
                                    autoComplete="off"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <MDInput
                                    label="담당자 연락처"
                                    fullWidth
                                    value={managerContact}
                                    onChange={(e: any) => this.handleChange("managerContact", e.target.value)}
                                    sx={inputSx}
                                    autoComplete="off"
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={isActive}
                                            onChange={(e) => this.handleChange("isActive", e.target.checked)}
                                            color="info"
                                        />
                                    }
                                    label={
                                        <MDTypography variant="button" fontWeight="regular" color="text">
                                            {isActive ? "활성" : "비활성"}
                                        </MDTypography>
                                    }
                                />
                            </Grid>
                        </Grid>
                    </MDBox>
                </DialogContent>
                <DialogActions sx={{ backgroundColor: backgroundColor, padding: "16px 24px" }}>
                    <MDButton onClick={onClose} color="dark">
                        취소
                    </MDButton>
                    <MDButton
                        variant="gradient"
                        color="info"
                        onClick={this.handleSubmit}
                        disabled={loading}
                    >
                        {loading ? "저장 중..." : (this.state.seqId ? "수정" : "등록")}
                    </MDButton>
                </DialogActions>
            </Dialog>
        );
    }
}

export default AddAdvertiserDialogClass;
